import { Strategy } from 'crono-fe-common/types/strategy';
import { StrategyCardWrapper } from './style';
import {
  ClickAwayListener,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import SettingsIcon from 'crono-fe-common/icons/Icon-Settings';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { VirtualElement } from '@popperjs/core';
import StrategyAutomationsModal, {
  StrategyAutomationsModalWidth,
} from 'context/strategyDetail/strategyAutomations/strategyAutomationsModal';
import { useStrategyAutomations } from 'context/strategyAutomations';
import { useAuth } from 'context/auth';
import PrivateLockIcon from 'crono-fe-common/icons/Icon-Private-Lock';
import useGetUsers from 'hooks/services/subscription/useGetUsers';
import { ReactComponent as NotOwnedIcon } from 'crono-fe-common/icons/Not-owned-by-the-user.svg';
import TagsTooltipForStrategy from './tagsTooltipForStrategy';
import { fullName } from 'crono-fe-common/utils';

const POPUP_OFFSETS = {
  m: {
    left: 250,
    right: 150,
  },
  l: {
    left: 350,
    right: 170,
  },
};

interface IProps {
  strategy: Strategy;
  hideAutomationsBtn?: boolean;
  automationsPopupOffset?: 'm' | 'l';
  hideOwnerSharedIcons?: boolean;
}

const StrategyCard = ({
  strategy,
  hideAutomationsBtn = false,
  automationsPopupOffset = 'm',
  hideOwnerSharedIcons = false,
}: IProps) => {
  const { user } = useAuth();
  const buttonRef = useRef<HTMLDivElement>(null); // Create a ref for the button

  const {
    setStrategyCardRandomIdForOpenedAutomations,
    strategyCardRandomIdForOpenedAutomations,
  } = useStrategyAutomations();

  //In order to have only 1 automation modal opened at the time we declare a randId for the strategyCard
  //so that we can check in the modal if the automations are opened for this strategy card (in case of resume
  //there may be 2 strategy cards with the same strategy id at the same time)
  const randId = useMemo(() => Math.random() * 10000000, []);

  const tooltipOpen = strategyCardRandomIdForOpenedAutomations === randId;
  const handleTooltipClose = () => {
    setStrategyCardRandomIdForOpenedAutomations(null);
  };

  const toggleTooltipOpen = useCallback(() => {
    setStrategyCardRandomIdForOpenedAutomations((prev) => {
      if (prev === randId) {
        return null;
      }
      return randId;
    });
  }, [setStrategyCardRandomIdForOpenedAutomations, randId]);

  const [anchorPosition, setAnchorPosition] = useState<VirtualElement | null>(
    null,
  );

  const calculateAnchorPosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spaceRight =
        window.innerWidth - (buttonRect.left + buttonRect.width);
      const spaceLeft = buttonRect.left;

      let tooltipLeft: number;
      const TOOLTIP_MARGIN_RIGHT = POPUP_OFFSETS[automationsPopupOffset].right;
      const TOOLTIP_MARGIN_LEFT = POPUP_OFFSETS[automationsPopupOffset].left;
      const TOOLTIP_WIDTH = StrategyAutomationsModalWidth;

      if (spaceRight >= TOOLTIP_WIDTH + TOOLTIP_MARGIN_RIGHT) {
        tooltipLeft = buttonRect.left + TOOLTIP_MARGIN_RIGHT;
      } else if (spaceLeft >= TOOLTIP_WIDTH + TOOLTIP_MARGIN_LEFT) {
        tooltipLeft = buttonRect.left - TOOLTIP_WIDTH - TOOLTIP_MARGIN_LEFT;
      } else {
        tooltipLeft = window.innerWidth / 2 - TOOLTIP_WIDTH / 2;
      }

      const top = window.innerHeight * 0.1; // 10vh from the top

      setAnchorPosition({
        getBoundingClientRect: () =>
          new DOMRect(tooltipLeft, top, TOOLTIP_WIDTH, 0),
      });
    }
  };

  useEffect(() => {
    calculateAnchorPosition();

    const handleResize = () => {
      if (tooltipOpen) {
        calculateAnchorPosition();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tooltipOpen]);
  const { data: users } = useGetUsers();

  const templateOwner = useMemo(() => {
    return users?.data?.data?.find((u) => u.id === strategy.userId);
  }, [users, strategy]);

  return (
    <StrategyCardWrapper>
      <div className="strategy-card-top">
        <div className="strategy-card-top__row">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {!hideOwnerSharedIcons && !strategy.shared && (
              <Tooltip
                arrow
                title={'Private sequence'}
                placement="top"
                PopperProps={{
                  sx: {
                    zIndex: 35002 + ' !important',
                  },
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    height: 'fit-content',
                    width: 'fit-content',
                  }}
                >
                  <PrivateLockIcon />
                </div>
              </Tooltip>
            )}
            {!hideOwnerSharedIcons && strategy.userId !== user?.id && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: '2px',
                }}
              >
                <Tooltip
                  arrow
                  title={
                    strategy.userId
                      ? `The owner is ${fullName(templateOwner?.firstName, templateOwner?.lastName) ?? 'deleted user'}`
                      : 'No owner'
                  }
                  placement="top"
                  PopperProps={{
                    sx: {
                      zIndex: 35002 + ' !important',
                    },
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      height: 'fit-content',
                      width: 'fit-content',
                    }}
                  >
                    <NotOwnedIcon className="not-owned-icon" />
                  </div>
                </Tooltip>
              </div>
            )}
            <Tooltip
              key={strategy.id}
              title={strategy.name}
              enterDelay={400}
              enterNextDelay={400}
              leaveDelay={0}
              placement={'top'}
              PopperProps={{
                sx: {
                  zIndex: 35002 + ' !important',
                },
              }}
              arrow
            >
              <Typography
                maxWidth={'370px'}
                fontSize={14}
                fontWeight={600}
                lineHeight={'22px'}
                textOverflow={'ellipsis'}
                overflow={'hidden'}
                whiteSpace={'nowrap'}
              >
                {strategy.name}
              </Typography>
            </Tooltip>
          </div>
        </div>

        <div className="strategy-card-top__row">
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {!!strategy.numberOfSteps && (
              <div className="steps-counter-container">
                {strategy.numberOfSteps} steps
              </div>
            )}

            <TagsTooltipForStrategy strategy={strategy} />
          </div>

          {!hideAutomationsBtn && (
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div
                style={{
                  display: 'flex',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <StrategyAutomationsCustomTooltip
                  title={<StrategyAutomationsModal strategyId={strategy.id} />}
                  onClose={handleTooltipClose}
                  open={tooltipOpen}
                  PopperProps={{
                    anchorEl: anchorPosition,
                    sx: {
                      zIndex: '35002 !important',
                    },
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: false,
                      },
                      {
                        name: 'preventOverflow',
                        enabled: false,
                      },
                    ],
                  }}
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                >
                  <div
                    className={'strategy-automations-button'}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleTooltipOpen();
                    }}
                    ref={buttonRef}
                  >
                    <SettingsIcon
                      size={16}
                      active={strategy.automationsEnabled}
                    />
                    <Typography
                      fontSize={11}
                      fontWeight={500}
                      lineHeight={'14px'}
                    >
                      Automations
                    </Typography>
                  </div>
                </StrategyAutomationsCustomTooltip>
              </div>
            </ClickAwayListener>
          )}
        </div>
      </div>
    </StrategyCardWrapper>
  );
};

export default StrategyCard;

const StrategyAutomationsCustomTooltip = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip
      arrow={props.arrow ? props.arrow : false}
      {...props}
      classes={{ popper: className }}
    />
  ),
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 0,
    height: '80vh',
    overflow: 'hidden',
    maxWidth: 'fit-content',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.10)',
  },
}));
